<template>
  <div>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur=""
      opacity="1"
      rounded="sm"
      :show="loading"
    >
      <b-card>
        <b-card-title>
          <h4>ETA by Service</h4>
        </b-card-title>

        <!-- Service Filter -->
        <b-row class="mb-3">
          <b-col md="6">
            <b-form-group label="Filter by Service">
              <v-select
                v-model="selectedService"
                :options="services"
                :reduce="service => service.id"
                label="id"
                placeholder="Select a service..."
                :clearable="true"
                @input="onServiceChange"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- ETA by Service Table -->
        <b-table
          responsive
          :items="tableData"
          :fields="tableColumns"
          show-empty
          empty-text="No ETA by Service data found"
          class="mt-2"
        >
          <template #cell(service)="data">
            <span v-if="data.item.service">
              {{ data.item.service.split('::')[1] }}
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(rideFare)="data">
            <span v-if="data.item.attributes && data.item.attributes.rideFare">
              {{ data.item.attributes.rideFare }} {{ data.item.attributes.currency || '' }}
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(timeTakes)="data">
            <span v-if="data.item.attributes && data.item.attributes.timeTakes">
              {{ Math.round(data.item.attributes.timeTakes / 60) }}
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(etaSeconds)="data">
            <span v-if="data.item.etaSeconds">
              {{ Math.round(data.item.etaSeconds / 60) }}
            </span>
            <span v-else>-</span>
          </template>
          <template #cell(pickUp)="data">
            <span>
              {{ data.item.attributes.pickupLatitude }}, {{ data.item.attributes.pickupLongitude }}
            </span>
          </template>
          <template #cell(dropOff)="data">
            <span>
              {{ data.item.attributes.dropoffLatitude }}, {{ data.item.attributes.dropoffLongitude }}
            </span>
          </template>

          <template #cell(createdAt)="data">
            {{ formatDate(data.item.createdAt) }}
          </template>
        </b-table>

        <!-- Pagination -->
        <base-pagination
          v-model="queryParams.page"
          :total="total"
          @new-per-page="queryParams.perPage = $event"
        />
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BTable,
  BOverlay,
  BRow,
  BCol,
  BFormGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  reactive,
  ref,
  computed,
  watch,
  toRef,
  onMounted,
} from '@vue/composition-api'
import BasePagination from '@/UI/BasePagination.vue'
import axiosIns from '@/libs/axios'
import { errorNotification } from '@/utils/toast-notifications'
import formatDate from '@/utils/date-format'

export default {
  components: {
    BCard,
    BCardTitle,
    BTable,
    BOverlay,
    BRow,
    BCol,
    BFormGroup,
    vSelect,
    BasePagination,
  },
  setup(props, ctx) {
    const loading = ref(false)
    const etaLogsData = ref([])
    const total = ref(0)
    const userId = ctx.root.$route.params.id
    const services = ref([])
    const selectedService = ref(null)

    const queryParams = reactive({
      page: 1,
      perPage: 10,
      userId,
      service: null,
    })

    const tableColumns = [
      {
        key: 'service',
        label: 'Service',
      },
      {
        key: 'rideFare',
        label: 'Ride Fare',
      },
      {
        key: 'timeTakes',
        label: 'Time Takes (in minutes)',
      },
      {
        key: 'pickUp',
        label: 'Pick Up',
      },
      {
        key: 'dropOff',
        label: 'Drop Off',
      },
      {
        key: 'etaSeconds',
        label: 'ETA Value',
      },
      {
        key: 'createdAt',
        label: 'Timestamp',
      },
    ]

    const tableData = computed(() => etaLogsData.value)

    const fetchServices = async () => {
      try {
        const response = await axiosIns.get('/services')
        console.log(response)
        services.value = response.data.data || []
      } catch (error) {
        console.error('Error fetching services:', error)
        errorNotification('Failed to fetch services')
        services.value = []
      }
    }

    const fetchEtaLogs = async () => {
      try {
        loading.value = true
        // Create clean params object without null values
        const cleanParams = Object.fromEntries(
          Object.entries(queryParams).filter(([, value]) => value !== null && value !== ''),
        )
        const response = await axiosIns.get('/users/eta-log', { params: cleanParams })
        etaLogsData.value = response.data.data.data.etas.filter(e => e.attributes !== null) || []
        total.value = response.data.data.data.total || 0
      } catch (error) {
        console.error('Error fetching ETA by Service data:', error)
        errorNotification('Failed to fetch ETA by Service data')
        etaLogsData.value = []
        total.value = 0
      } finally {
        loading.value = false
      }
    }

    const onServiceChange = value => {
      queryParams.service = value
      queryParams.page = 1 // Reset to first page when filter changes
      fetchEtaLogs()
    }

    const refreshLogs = () => {
      fetchEtaLogs()
    }

    const getServiceLabel = serviceId => {
      const service = services.value.find(s => s.id === serviceId)
      return service ? service.serviceLabel : serviceId || '-'
    }

    // Watch for pagination changes
    watch([toRef(queryParams, 'page'), toRef(queryParams, 'perPage')], () => {
      fetchEtaLogs()
    })

    // Initial fetch
    onMounted(() => {
      fetchServices()
      fetchEtaLogs()
    })

    return {
      loading,
      queryParams,
      tableColumns,
      tableData,
      total,
      services,
      selectedService,
      fetchEtaLogs,
      onServiceChange,
      refreshLogs,
      formatDate,
      getServiceLabel,
    }
  },
}
</script>

<style scoped>
.btn-icon {
  height: 2.214rem;
  width: 2.214rem;
}
</style>
